/**
 * Analysis Exception Management — canonical component
 * mode="dashboard"   → operational (Retry, Resolve per item; Retry Selected bulk; no deletion)
 * mode="management"  → full admin (all bulk ops, delete, full filters, metrics)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const REASON_LABELS = {
  server_restart:      "Server restart",
  timeout:             "Processing timeout",
  missing_transcript:  "Missing transcript",
  no_speech_detected:  "No speech detected",
  rate_limited:        "Rate limited",
  llm_error:           "LLM timeout",
  invalid_ai_response: "Invalid AI response",
  manual_cancellation: "Manual cancellation",
  processing_exception:"Processing exception",
  unknown:             "Unknown",
};

const REASON_TONE = {
  server_restart:      "bg-slate-100 text-slate-600 border-slate-200",
  timeout:             "bg-amber-50 text-amber-700 border-amber-200",
  missing_transcript:  "bg-rose-50 text-rose-700 border-rose-200",
  no_speech_detected:  "bg-orange-50 text-orange-700 border-orange-200",
  rate_limited:        "bg-purple-50 text-purple-700 border-purple-200",
  llm_error:           "bg-blue-50 text-blue-700 border-blue-200",
  invalid_ai_response: "bg-red-50 text-red-700 border-red-200",
  processing_exception:"bg-slate-100 text-slate-600 border-slate-200",
  unknown:             "bg-slate-100 text-slate-500 border-slate-100",
};

function relTime(iso) {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function MetricChip({ label, value, tone = "text-slate-700 bg-slate-100" }) {
  return (
    <div className={cn("inline-flex flex-col items-center justify-center px-3 py-2 rounded-xl text-center min-w-[88px]", tone)}>
      <span className="text-lg font-bold leading-none">{value ?? "—"}</span>
      <span className="text-[10px] font-medium opacity-70 mt-0.5 leading-tight">{label}</span>
    </div>
  );
}

function ReasonChip({ reason }) {
  const label = REASON_LABELS[reason] || reason || "Unknown";
  const tone  = REASON_TONE[reason] || REASON_TONE.unknown;
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border", tone)}>
      {label}
    </span>
  );
}

function ConfirmDialog({ open, onClose, title, description, onConfirm, withNote = false, confirmLabel = "Proceed", variant = "amber" }) {
  const [note, setNote] = useState("");
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.AlertTriangle className="h-4 w-4 text-amber-500" />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {withNote && (
          <textarea
            rows={2}
            placeholder="Resolution note (optional)"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none"
            value={note}
            onChange={e => setNote(e.target.value)}
            data-testid="aeq-confirm-note"
          />
        )}
        <DialogFooter className="gap-2 pt-1">
          <button
            onClick={onClose}
            data-testid="aeq-confirm-cancel"
            className="h-8 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={() => { onConfirm(note); setNote(""); }}
            data-testid="aeq-confirm-proceed"
            className={cn("h-8 px-4 rounded-lg text-sm font-semibold text-white",
              variant === "danger" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-500 hover:bg-amber-600")}>
            {confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AnalysisExceptionQueue({ mode = "management" }) {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const isDashboard = mode === "dashboard";
  const isMgmt      = ["administrator", "partner", "manager"].includes(user?.role);

  // ── Data ────────────────────────────────────────────────────────────────────
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(1);
  const PAGE_SIZE = isDashboard ? 10 : 20;

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("active");
  const [reasonFilter, setReasonFilter] = useState("");
  const [clientQ,      setClientQ]      = useState("");
  const [minRetries,   setMinRetries]   = useState("");

  // ── Selection ────────────────────────────────────────────────────────────────
  const [selected,    setSelected]  = useState(new Set());
  const allSelected = items.length > 0 && selected.size === items.length;

  // ── Confirm + busy ────────────────────────────────────────────────────────────
  const [confirm, setConfirm] = useState(null);
  const [busy,    setBusy]    = useState(null);

  // ── Metrics ──────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, page, page_size: PAGE_SIZE });
      if (reasonFilter) params.append("failure_reason", reasonFilter);
      if (clientQ.trim()) params.append("client_q", clientQ.trim());
      if (minRetries) params.append("min_retries", minRetries);
      // Sequential: in-progress runs first (triggers auto-retirement server-side),
      // then exceptions fetches — belt-and-suspenders defence against race conditions.
      const statsR = await api.get("/case-analysis/queue/in-progress");
      const excR   = await api.get(`/case-analysis/exceptions?${params}`);
      setItems(excR.data?.items || []);
      setTotal(excR.data?.total || 0);
      setStats(statsR.data?.stats || null);
    } catch {
      toast.error("Failed to load exception queue");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter, clientQ, minRetries, page, PAGE_SIZE]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [statusFilter, reasonFilter, clientQ, minRetries]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const bulkAction = async (action, ids = null, note = "") => {
    setBusy(action);
    try {
      const body = { action, note };
      if (ids !== null) body.ids = ids;
      const r = await api.post("/case-analysis/bulk-action", body);
      const d = r.data;
      if (action.includes("resolve")) toast.success(`Resolved ${d.resolved ?? d.succeeded ?? 0} analyses`);
      else if (action === "retry" || action === "retry_all") toast.success(`Queued ${d.succeeded ?? 0} retries`);
      else if (action === "delete") toast.success(`Deleted ${d.succeeded ?? 0} analyses`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(null); setConfirm(null);
    }
  };

  const singleAction = async (action, id, note = "") => {
    setBusy(id + action);
    try {
      if (action === "resolve")      await api.post(`/case-analysis/${id}/resolve`, { note });
      else if (action === "delete")  await api.delete(`/case-analysis/${id}`);
      else if (action === "retry")   await api.post(`/case-analysis/${id}/retry`);
      // Optimistically remove resolved/deleted items immediately so the list
      // stays consistent even if the background reload is delayed.
      if (action === "resolve" || action === "delete") {
        setItems(prev => prev.filter(i => i.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
        setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
      toast.success(
        action === "delete"  ? "Analysis deleted" :
        action === "resolve" ? "Resolved" : "Retry queued"
      );
      load();
    } catch (e) {
      const errDetail = e.response?.data?.detail || `${action} failed`;
      toast.error(errDetail);
      // If the backend reports a stale-state conflict (item already resolved/retired),
      // auto-refresh so the queue no longer shows it as actionable.
      if (e.response?.status === 400 && errDetail.includes("current status:")) {
        load();
      }
    } finally {
      setBusy(null); setConfirm(null);
    }
  };

  const selIds    = [...selected];
  const hasSel    = selIds.length > 0;
  const selFailed = items.filter(i => selected.has(i.id) && ["failed", "failed_recoverable"].includes(i.status));

  return (
    <div className="space-y-4" data-testid="analysis-exception-queue" data-mode={mode}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <h2 className="font-heading text-lg font-bold text-[#171B2E] flex items-center gap-2">
          <Icons.AlertCircle className="h-5 w-5 text-amber-500" />
          {isDashboard ? "Analysis Exceptions" : "Analysis Exception Management"}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {isDashboard
            ? "Failed analyses requiring attention — retry to re-run or resolve to dismiss."
            : "Govern failed analyses — retry, resolve, or remove. Transcript and meeting data are never affected."}
        </p>
      </div>

      {/* ── Metrics ────────────────────────────────────────────────────────── */}
      {stats && (
        <div className="flex flex-wrap gap-2" data-testid="aeq-metrics">
          <MetricChip label="Active Exceptions" value={stats.pending_retry}
            tone={stats.pending_retry > 0 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"} />
          <MetricChip label="Currently Processing" value={stats.processing} tone="bg-blue-50 text-blue-700" />
          <MetricChip label="Completed Today" value={stats.completed_today} tone="bg-emerald-50 text-emerald-700" />
          <MetricChip label="Resolved Today" value={stats.resolved_today} tone="bg-slate-100 text-slate-600" />
          <MetricChip label="Historical Exceptions" value={stats.resolved_total} tone="bg-slate-100 text-slate-500" />
        </div>
      )}

      {/* ── Filters + Top Actions ─────────────────────────────────────────── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          {/* Status filter — always shown */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
            data-testid="aeq-filter-status">
            <option value="active">Active Exceptions</option>
            <option value="resolved">Historical Exceptions</option>
            <option value="all">All</option>
          </select>

          {/* Failure reason — management only */}
          {!isDashboard && (
            <select
              value={reasonFilter}
              onChange={e => setReasonFilter(e.target.value)}
              className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700"
              data-testid="aeq-filter-reason">
              <option value="">All Reasons</option>
              {Object.entries(REASON_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          )}

          {/* Client search — always shown */}
          <div className="relative">
            <Icons.Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              value={clientQ}
              onChange={e => setClientQ(e.target.value)}
              placeholder="Search client..."
              className="h-8 pl-7 pr-3 rounded-lg border border-slate-200 bg-white text-sm w-36"
              data-testid="aeq-filter-client" />
          </div>

          {/* Min retries — management only */}
          {!isDashboard && (
            <input
              type="number" min={0}
              value={minRetries}
              onChange={e => setMinRetries(e.target.value)}
              placeholder="Min retries"
              className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-sm w-24"
              data-testid="aeq-filter-retries" />
          )}

          <div className="flex-1" />

          {/* Quick bulk actions */}
          {statusFilter === "active" && (
            <>
              <button
                onClick={() => setConfirm({ type: "retry_all", label: "Retry All", desc: "Re-queue all active failed analyses." })}
                disabled={!!busy}
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                data-testid="aeq-retry-all">
                <Icons.RefreshCw className="h-3.5 w-3.5" />Retry All
              </button>
              {/* Resolve All — management mode only */}
              {!isDashboard && (
                <button
                  onClick={() => setConfirm({ type: "resolve_all", label: "Resolve All", desc: "Mark all active failed analyses as resolved.", withNote: true })}
                  disabled={!!busy}
                  className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                  data-testid="aeq-resolve-all">
                  <Icons.CheckCircle className="h-3.5 w-3.5 text-emerald-600" />Resolve All
                </button>
              )}
            </>
          )}
        </div>

        {/* Bulk action bar — appears when items are selected */}
        {hasSel && (
          <div
            className="flex items-center gap-2 bg-white border border-blue-200 rounded-xl px-3 py-2"
            data-testid="aeq-bulk-bar">
            <span className="text-xs font-semibold text-blue-700">{selIds.length} selected</span>
            <div className="h-4 w-px bg-slate-200" />
            <button
              onClick={() => setConfirm({ type: "retry_sel", label: "Retry Selected", desc: `Re-queue ${selFailed.length} selected analyses.` })}
              disabled={selFailed.length === 0 || !!busy}
              className="h-7 px-2.5 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
              data-testid="aeq-bulk-retry">
              <Icons.RefreshCw className="h-3 w-3" />Retry
            </button>
            {/* Resolve Selected — management only */}
            {!isDashboard && (
              <button
                onClick={() => setConfirm({ type: "resolve_sel", label: "Resolve Selected", desc: `Mark ${selIds.length} as resolved.`, withNote: true })}
                disabled={!!busy}
                className="h-7 px-2.5 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1"
                data-testid="aeq-bulk-resolve">
                <Icons.CheckCircle className="h-3 w-3 text-emerald-600" />Resolve
              </button>
            )}
            {/* Delete Selected — management + admin/partner/manager only */}
            {!isDashboard && isMgmt && (
              <button
                onClick={() => setConfirm({ type: "delete_sel", label: "Delete Selected", desc: `Delete ${selIds.length} analysis records. Transcripts, recordings, and meetings are preserved.`, variant: "danger" })}
                disabled={!!busy}
                className="h-7 px-2.5 rounded-lg text-xs font-semibold border border-rose-200 text-rose-600 bg-white hover:bg-rose-50 flex items-center gap-1"
                data-testid="aeq-bulk-delete">
                <Icons.Trash2 className="h-3 w-3" />Delete
              </button>
            )}
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-[11px] text-slate-400 hover:text-slate-600"
              data-testid="aeq-bulk-clear">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Items list ─────────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        {/* Select all header */}
        {items.length > 0 && (
          <div className="flex items-center gap-2 px-1 pb-1">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => setSelected(allSelected ? new Set() : new Set(items.map(i => i.id)))}
              className="h-3.5 w-3.5 rounded border-slate-300 accent-blue-600"
              data-testid="aeq-select-all" />
            <span className="text-[11px] text-slate-400">{allSelected ? "Deselect all" : "Select all"}</span>
            <span className="ml-auto text-[11px] text-slate-400">
              {total} total{total !== items.length ? ` (showing ${items.length})` : ""}
            </span>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <Icons.Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-10 text-slate-400" data-testid="aeq-empty">
            <Icons.CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-300" />
            <div className="text-sm font-medium">No exceptions</div>
            <div className="text-xs mt-0.5">
              {statusFilter === "active"
                ? "All analyses are healthy or resolved."
                : "No historical exceptions found."}
            </div>
          </div>
        )}

        {items.map(a => (
          <ExceptionCard
            key={a.id}
            item={a}
            mode={mode}
            selected={selected.has(a.id)}
            onSelect={checked => setSelected(prev => {
              const n = new Set(prev);
              checked ? n.add(a.id) : n.delete(a.id);
              return n;
            })}
            isMgmt={isMgmt}
            busy={busy}
            onRetry={() => setConfirm({ type: "retry_one", id: a.id, label: "Retry Analysis", desc: "Re-queue this analysis for Allison to process." })}
            onResolve={() => setConfirm({ type: "resolve_one", id: a.id, label: "Resolve Analysis", desc: "Mark as resolved. No further retries needed. Transcript and meeting data are preserved.", withNote: true })}
            onDelete={() => setConfirm({ type: "delete_one", id: a.id, label: "Delete Analysis", desc: "Delete only the failed analysis record. Zoom recording, transcript, and meeting data are preserved and a new analysis can be started in the future.", variant: "danger" })}
            onView={() => navigate(`/case-analysis/${a.client_id}`)}
          />
        ))}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 pt-2" data-testid="aeq-pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40">
            <Icons.ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / PAGE_SIZE)}</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / PAGE_SIZE)}
            className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40">
            <Icons.ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Confirm dialogs ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.label || ""}
        description={confirm?.desc}
        withNote={confirm?.withNote}
        confirmLabel={
          confirm?.type?.includes("delete")  ? "Delete" :
          confirm?.type?.includes("resolve") ? "Resolve" :
          confirm?.type?.includes("retry")   ? "Retry"  : "Proceed"
        }
        variant={confirm?.variant || "amber"}
        onConfirm={async (note) => {
          const t = confirm?.type;
          if      (t === "retry_all")   await bulkAction("retry_all");
          else if (t === "resolve_all") await bulkAction("resolve_all", null, note);
          else if (t === "retry_sel")   await bulkAction("retry", selIds);
          else if (t === "resolve_sel") await bulkAction("resolve", selIds, note);
          else if (t === "delete_sel")  await bulkAction("delete", selIds);
          else if (t === "retry_one")   await singleAction("retry",   confirm.id);
          else if (t === "resolve_one") await singleAction("resolve", confirm.id, note);
          else if (t === "delete_one")  await singleAction("delete",  confirm.id);
        }}
      />
    </div>
  );
}

// ── Exception Card ─────────────────────────────────────────────────────────────
function ExceptionCard({ item: a, mode, selected, onSelect, isMgmt, busy, onRetry, onResolve, onDelete, onView }) {
  const isDashboard = mode === "dashboard";
  const isActive = ["failed", "failed_recoverable"].includes(a.status);
  const isBusy   = busy === a.id + "retry" || busy === a.id + "resolve" || busy === a.id + "delete";

  return (
    <div
      className={cn(
        "flex items-start gap-3 bg-white border rounded-xl px-3.5 py-3 group transition-colors",
        selected ? "border-blue-300 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"
      )}
      data-testid={`aeq-item-${a.id}`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={e => onSelect(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 accent-blue-600 shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-800">{a.client_name}</span>
          {a.client_ref && <span className="text-[11px] text-slate-400">#{a.client_ref}</span>}
          <ReasonChip reason={a.failure_reason} />
          {a.status === "resolved" && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
              Resolved
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-400">
          {a.retry_count > 0 && (
            <span className="font-medium text-orange-500" data-testid={`aeq-retry-count-${a.id}`}>
              {a.retry_count} {a.retry_count === 1 ? "retry" : "retries"}
            </span>
          )}
          <span>Failed {relTime(a.last_failed_at || a.updated_at)}</span>
          {a.last_retry_at && <span>Last retry {relTime(a.last_retry_at)}</span>}
          {a.transcript_source && <span className="truncate max-w-[200px]">{a.transcript_source}</span>}
          {a.status === "resolved" && a.resolved_by_name && (
            <span>Resolved by {a.resolved_by_name}</span>
          )}
        </div>
        {a.resolution_note && (
          <p className="text-[11px] italic text-slate-400 mt-0.5 truncate">{a.resolution_note}</p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {isBusy ? (
          <Icons.Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        ) : (
          <>
            {/* Retry — both modes, active only */}
            {isActive && (
              <button
                onClick={onRetry}
                className="h-7 px-2.5 rounded-lg text-xs font-semibold border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1"
                data-testid={`aeq-retry-${a.id}`}>
                <Icons.RefreshCw className="h-3 w-3" />Retry
              </button>
            )}
            {/* Resolve — both modes, active only */}
            {isActive && (
              <button
                onClick={onResolve}
                className="h-7 px-2.5 rounded-lg text-xs font-semibold border border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-50 flex items-center gap-1"
                data-testid={`aeq-resolve-${a.id}`}>
                <Icons.Check className="h-3 w-3" />Resolve
              </button>
            )}
            {/* View Details — dashboard mode link to case analysis page */}
            {isDashboard && isActive && (
              <button
                onClick={onView}
                className="h-7 px-2 rounded-lg border border-slate-100 text-slate-400 bg-white hover:bg-slate-50 hover:text-slate-600"
                data-testid={`aeq-view-${a.id}`}
                title="View case analysis">
                <Icons.ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Delete — management mode, mgmt role only, hover-reveal */}
            {!isDashboard && isMgmt && (
              <button
                onClick={onDelete}
                className="h-7 px-2 rounded-lg border border-rose-100 text-rose-500 bg-white hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`aeq-delete-${a.id}`}
                title="Delete analysis record">
                <Icons.Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
